/**
 * Properties Panel Component
 * 
 * Right panel showing properties editor for the selected step, variant, or field.
 */

import { useState, useEffect } from 'react';
import {
  Settings,
  Type,
  Shield,
  X,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  useFormBuilder,
  useSelectedStep,
  useSelectedVariant,
  useSelectedField,
} from '../../contexts';
import type {
  FormField,
  FormStep,
  StepVariant,
  FieldOption,
  ValidationRules,
} from '@formflow/shared';
import './PropertiesPanel.css';

// =============================================================================
// Collapsible Section
// =============================================================================

interface SectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Section({ title, defaultOpen = true, children }: SectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`properties-section ${isOpen ? 'open' : ''}`}>
      <button
        className="section-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span>{title}</span>
      </button>
      {isOpen && <div className="section-content">{children}</div>}
    </div>
  );
}

// =============================================================================
// Field Properties Editor
// =============================================================================

interface FieldPropertiesProps {
  field: FormField;
  onUpdate: (updates: Partial<FormField>) => void;
}

function FieldProperties({ field, onUpdate }: FieldPropertiesProps) {
  const [localField, setLocalField] = useState(field);
  const [options, setOptions] = useState<FieldOption[]>(field.options || []);

  // Sync with external field changes
  useEffect(() => {
    setLocalField(field);
    setOptions(field.options || []);
  }, [field]);

  const handleChange = (key: keyof FormField, value: unknown) => {
    setLocalField((prev) => ({ ...prev, [key]: value }));
  };

  const handleBlur = (key: keyof FormField) => {
    if (localField[key] !== field[key]) {
      onUpdate({ [key]: localField[key] });
    }
  };

  const handleValidationChange = (key: keyof ValidationRules, value: unknown) => {
    const newRules = { ...localField.validation_rules, [key]: value };
    handleChange('validation_rules', newRules);
    onUpdate({ validation_rules: newRules });
  };

  const handleAddOption = () => {
    const newOptions = [...options, { value: '', label: '' }];
    setOptions(newOptions);
    onUpdate({ options: newOptions });
  };

  const handleUpdateOption = (index: number, key: keyof FieldOption, value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [key]: value };
    setOptions(newOptions);
    onUpdate({ options: newOptions });
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
    onUpdate({ options: newOptions });
  };

  const hasOptions = ['select', 'radio', 'checkbox'].includes(field.field_type);

  return (
    <div className="field-properties">
      <Section title="Basic Settings">
        <div className="form-group">
          <label htmlFor="field-label">Label</label>
          <input
            id="field-label"
            type="text"
            value={localField.label}
            onChange={(e) => handleChange('label', e.target.value)}
            onBlur={() => handleBlur('label')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="field-name">Field Name</label>
          <input
            id="field-name"
            type="text"
            value={localField.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            className="mono"
          />
          <span className="helper-text">Used in submissions data</span>
        </div>

        <div className="form-group">
          <label htmlFor="field-placeholder">Placeholder</label>
          <input
            id="field-placeholder"
            type="text"
            value={localField.placeholder || ''}
            onChange={(e) => handleChange('placeholder', e.target.value)}
            onBlur={() => handleBlur('placeholder')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="field-help">Help Text</label>
          <textarea
            id="field-help"
            value={localField.help_text || ''}
            onChange={(e) => handleChange('help_text', e.target.value)}
            onBlur={() => handleBlur('help_text')}
            rows={2}
          />
        </div>

        <div className="form-group">
          <label htmlFor="field-default">Default Value</label>
          <input
            id="field-default"
            type="text"
            value={localField.default_value || ''}
            onChange={(e) => handleChange('default_value', e.target.value)}
            onBlur={() => handleBlur('default_value')}
          />
        </div>
      </Section>

      {hasOptions && (
        <Section title="Options">
          <div className="options-list">
            {options.map((option, index) => (
              <div key={index} className="option-row">
                <input
                  type="text"
                  placeholder="Value"
                  value={option.value}
                  onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                  className="option-value"
                />
                <input
                  type="text"
                  placeholder="Label"
                  value={option.label}
                  onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                  className="option-label"
                />
                <button
                  className="btn-icon-sm"
                  onClick={() => handleRemoveOption(index)}
                  aria-label="Remove option"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button className="btn btn-sm btn-ghost" onClick={handleAddOption}>
              <Plus size={14} />
              Add Option
            </button>
          </div>
        </Section>
      )}

      <Section title="Validation">
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={localField.required}
              onChange={(e) => {
                handleChange('required', e.target.checked);
                onUpdate({ required: e.target.checked });
              }}
            />
            <span>Required</span>
          </label>
        </div>

        {['text', 'textarea', 'email', 'phone'].includes(field.field_type) && (
          <>
            <div className="form-group">
              <label htmlFor="min-length">Min Length</label>
              <input
                id="min-length"
                type="number"
                min="0"
                value={localField.validation_rules?.min_length || ''}
                onChange={(e) =>
                  handleValidationChange('min_length', e.target.value ? parseInt(e.target.value) : undefined)
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="max-length">Max Length</label>
              <input
                id="max-length"
                type="number"
                min="0"
                value={localField.validation_rules?.max_length || ''}
                onChange={(e) =>
                  handleValidationChange('max_length', e.target.value ? parseInt(e.target.value) : undefined)
                }
              />
            </div>
          </>
        )}

        {field.field_type === 'number' && (
          <>
            <div className="form-group">
              <label htmlFor="min-value">Min Value</label>
              <input
                id="min-value"
                type="number"
                value={localField.validation_rules?.min_value ?? ''}
                onChange={(e) =>
                  handleValidationChange('min_value', e.target.value ? parseFloat(e.target.value) : undefined)
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="max-value">Max Value</label>
              <input
                id="max-value"
                type="number"
                value={localField.validation_rules?.max_value ?? ''}
                onChange={(e) =>
                  handleValidationChange('max_value', e.target.value ? parseFloat(e.target.value) : undefined)
                }
              />
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="pattern">Regex Pattern</label>
          <input
            id="pattern"
            type="text"
            value={localField.validation_rules?.pattern || ''}
            onChange={(e) => handleValidationChange('pattern', e.target.value || undefined)}
            placeholder="e.g., ^[A-Z]+$"
            className="mono"
          />
        </div>

        <div className="form-group">
          <label htmlFor="pattern-message">Pattern Error Message</label>
          <input
            id="pattern-message"
            type="text"
            value={localField.validation_rules?.pattern_message || ''}
            onChange={(e) => handleValidationChange('pattern_message', e.target.value || undefined)}
          />
        </div>
      </Section>

      <Section title="Privacy & Security">
        <div className="form-group checkbox-group">
          <label className="checkbox-label phi-checkbox">
            <input
              type="checkbox"
              checked={localField.is_phi}
              onChange={(e) => {
                handleChange('is_phi', e.target.checked);
                onUpdate({ is_phi: e.target.checked });
              }}
            />
            <Shield size={16} />
            <span>Protected Health Information (PHI)</span>
          </label>
          <span className="helper-text">
            PHI fields are encrypted at rest and require MFA to view submissions.
          </span>
        </div>
      </Section>
    </div>
  );
}

// =============================================================================
// Step Properties Editor
// =============================================================================

interface StepPropertiesProps {
  step: FormStep;
  onUpdate: (updates: Partial<FormStep>) => void;
}

function StepProperties({ step, onUpdate }: StepPropertiesProps) {
  const [localStep, setLocalStep] = useState(step);

  useEffect(() => {
    setLocalStep(step);
  }, [step]);

  const handleChange = (key: keyof FormStep, value: unknown) => {
    setLocalStep((prev) => ({ ...prev, [key]: value }));
  };

  const handleBlur = (key: keyof FormStep) => {
    if (localStep[key] !== step[key]) {
      onUpdate({ [key]: localStep[key] });
    }
  };

  return (
    <div className="step-properties">
      <Section title="Step Settings">
        <div className="form-group">
          <label htmlFor="step-name">Step Name</label>
          <input
            id="step-name"
            type="text"
            value={localStep.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="step-description">Description</label>
          <textarea
            id="step-description"
            value={localStep.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            onBlur={() => handleBlur('description')}
            rows={3}
          />
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={localStep.is_skippable}
              onChange={(e) => {
                handleChange('is_skippable', e.target.checked);
                onUpdate({ is_skippable: e.target.checked });
              }}
            />
            <span>Allow users to skip this step</span>
          </label>
        </div>
      </Section>
    </div>
  );
}

// =============================================================================
// Variant Properties Editor
// =============================================================================

interface VariantPropertiesProps {
  variant: StepVariant;
  onUpdate: (updates: Partial<StepVariant>) => void;
  onDelete: () => void;
  canDelete: boolean;
}

function VariantProperties({
  variant,
  onUpdate,
  onDelete,
  canDelete,
}: VariantPropertiesProps) {
  const [localVariant, setLocalVariant] = useState(variant);

  useEffect(() => {
    setLocalVariant(variant);
  }, [variant]);

  const handleChange = (key: keyof StepVariant, value: unknown) => {
    setLocalVariant((prev) => ({ ...prev, [key]: value }));
  };

  const handleBlur = (key: keyof StepVariant) => {
    if (localVariant[key] !== variant[key]) {
      onUpdate({ [key]: localVariant[key] });
    }
  };

  return (
    <div className="variant-properties">
      <Section title="Variant Settings">
        <div className="form-group">
          <label htmlFor="variant-name">Variant Name</label>
          <input
            id="variant-name"
            type="text"
            value={localVariant.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="variant-weight">Traffic Weight (%)</label>
          <input
            id="variant-weight"
            type="number"
            min="0"
            max="100"
            value={localVariant.weight}
            onChange={(e) => handleChange('weight', parseInt(e.target.value) || 0)}
            onBlur={() => handleBlur('weight')}
          />
          <span className="helper-text">
            Percentage of traffic to show this variant
          </span>
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={localVariant.is_control}
              onChange={(e) => {
                handleChange('is_control', e.target.checked);
                onUpdate({ is_control: e.target.checked });
              }}
            />
            <span>Control Variant</span>
          </label>
          <span className="helper-text">
            The control is used as the baseline for A/B test comparison
          </span>
        </div>
      </Section>

      {canDelete && (
        <Section title="Danger Zone" defaultOpen={false}>
          <button
            className="btn btn-sm btn-danger btn-full"
            onClick={() => {
              if (window.confirm(`Delete variant "${variant.name}"? This cannot be undone.`)) {
                onDelete();
              }
            }}
          >
            <Trash2 size={14} />
            Delete Variant
          </button>
        </Section>
      )}
    </div>
  );
}

// =============================================================================
// Properties Panel
// =============================================================================

export function PropertiesPanel() {
  const { state, updateStep, updateVariant, updateField, deleteVariant, selectField } =
    useFormBuilder();
  const { selectedFieldId } = state;
  const step = useSelectedStep();
  const variant = useSelectedVariant();
  const field = useSelectedField();

  // Determine what to show
  const showField = !!field;
  const showVariant = !showField && variant && step && step.variants.length > 1;
  const showStep = !showField && !showVariant && !!step;
  const showEmpty = !step;

  return (
    <aside className="properties-panel">
      <div className="panel-header">
        <h2 className="panel-title">
          <Settings size={18} />
          Properties
        </h2>
        {selectedFieldId && (
          <button
            className="btn-icon-sm"
            onClick={() => selectField(null)}
            aria-label="Close field properties"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="panel-content">
        {showEmpty && (
          <div className="properties-empty">
            <Type size={32} />
            <p>Select a step or field to edit its properties</p>
          </div>
        )}

        {showStep && step && (
          <StepProperties
            step={step}
            onUpdate={(updates) => updateStep(step.id, updates)}
          />
        )}

        {showVariant && variant && step && (
          <VariantProperties
            variant={variant}
            onUpdate={(updates) => updateVariant(variant.id, updates)}
            onDelete={() => deleteVariant(variant.id)}
            canDelete={step.variants.length > 1}
          />
        )}

        {showField && field && (
          <FieldProperties
            field={field}
            onUpdate={(updates) => updateField(field.id, updates)}
          />
        )}
      </div>
    </aside>
  );
}

export default PropertiesPanel;
